import api from './client';
import {
  SleeperAccount,
  SleeperAccountWithHistory,
  SleeperProfile,
  League,
  Leaguemate,
} from '../types';

export const usersApi = {
  // Get current user's profile
  async getProfile() {
    const { data } = await api.get('/users/profile');
    return data;
  },

  // Get user's sleeper accounts
  async getSleeperAccounts(): Promise<SleeperAccountWithHistory[]> {
    const { data } = await api.get<SleeperAccountWithHistory[]>('/users/sleeper-accounts');
    return data;
  },

  // Link a Sleeper account
  async linkSleeperAccount(username: string): Promise<{
    account: SleeperAccount;
    message: string;
    verificationCode: string;
  }> {
    const { data } = await api.post('/users/link-sleeper', { username });
    return data;
  },

  // Sync a sleeper account
  async syncSleeperAccount(accountId: string): Promise<SleeperAccount> {
    const { data } = await api.post<SleeperAccount>(
      `/users/sleeper-accounts/${accountId}/sync`
    );
    return data;
  },

  // Sync the CURRENT season's leagues for a sleeper account (fast path).
  async syncLeagues(accountId: string): Promise<{ leagues: League[]; count: number }> {
    const { data } = await api.post(`/users/sleeper-accounts/${accountId}/sync-leagues`);
    return data;
  },

  // Kick off a background multi-season deep-sync. Returns immediately; the
  // frontend can then poll getSleeperAccounts() to watch leagues appear.
  async deepSyncLeagues(accountId: string): Promise<{ started: boolean }> {
    const { data } = await api.post(`/users/sleeper-accounts/${accountId}/deep-sync`);
    return data;
  },

  // Get leagues for a sleeper account
  async getAccountLeagues(accountId: string): Promise<League[]> {
    const { data } = await api.get<League[]>(`/users/sleeper-accounts/${accountId}/leagues`);
    return data;
  },

  // Search users
  async searchUsers(query: string, options?: { limit?: number; offset?: number }): Promise<SleeperAccount[]> {
    const { data } = await api.get<SleeperAccount[]>('/users/search', {
      params: { q: query, ...options },
    });
    return data;
  },

  // Lookup user from Sleeper API (creates in DB if not exists)
  async lookupUser(username: string): Promise<SleeperAccount> {
    const { data } = await api.get<SleeperAccount>(`/users/lookup/${encodeURIComponent(username)}`);
    return data;
  },

  // Get sleeper profile by user ID
  async getSleeperProfile(sleeperUserId: string): Promise<SleeperProfile> {
    const { data } = await api.get<SleeperProfile>(`/users/sleeper/${sleeperUserId}`);
    return data;
  },

  // Get all leaguemates of the current user, sorted server-side by
  // shared league count. Powers the Rate Your Leaguemates page.
  async getLeaguemates(): Promise<Leaguemate[]> {
    const { data } = await api.get<Leaguemate[]>('/users/leaguemates');
    return data;
  },
};

